import React, { useState } from 'react'
import { Head, Link, usePage } from '@inertiajs/react'
import { ArrowLeft, Briefcase, Building2, CalendarDays, ChevronRight, Download, FileText, Globe, LayoutDashboard, Users, ClipboardCheck, ClipboardList, Shield, Settings, Stamp } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SharedProps } from '@/types'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'

type BreadcrumbEntry = {
  label: string
  href?: string
}

type BackofficeLayoutProps = {
  children: React.ReactNode
  title: string
  breadcrumbs?: BreadcrumbEntry[]
  contentWidth?: 'default' | 'wide' | 'full'
}

type NavEntry =
  | { type: 'link'; href: string; icon: typeof LayoutDashboard; label: string; requiredRole?: 'admin' | 'all' }
  | { type: 'group'; icon: typeof LayoutDashboard; label: string; children: { href: string; label: string }[]; requiredRole?: 'admin' | 'all' }

type NavSection = {
  label: string
  items: NavEntry[]
  requiredRole?: 'admin' | 'all'
}

const NAV_SECTIONS: NavSection[] = [
  {
    label: '',
    items: [
      { type: 'link', href: '/backoffice/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { type: 'link', href: '/backoffice/calendar', icon: CalendarDays, label: 'Calendar' },
    ],
  },
  {
    label: 'User Services',
    items: [
      { type: 'link', href: '/backoffice/businesses', icon: Briefcase, label: 'Businesses' },
      { type: 'link', href: '/backoffice/formations', icon: Building2, label: 'Formations' },
      {
        type: 'group', icon: ClipboardCheck, label: 'Compliance',
        children: [
          { href: '/backoffice/compliance_filings', label: 'Compliance Filings' },
          { href: '/backoffice/tax_registrations', label: 'Tax Registrations' },
        ],
      },
      { type: 'link', href: '/backoffice/licenses', icon: FileText, label: 'Licenses' },
      {
        type: 'group', icon: Users, label: 'Nominees',
        children: [
          { href: '/backoffice/nominee_directors', label: 'Nominee Directors' },
          { href: '/backoffice/nominee_shareholders', label: 'Nominee Shareholders' },
        ],
      },
      {
        type: 'group', icon: Shield, label: 'Registered Agent',
        children: [
          { href: '/backoffice/registered_agents', label: 'Registered Agents' },
          { href: '/backoffice/registered_addresses', label: 'Registered Addresses' },
        ],
      },
      { type: 'link', href: '/backoffice/business_tasks', icon: ClipboardList, label: 'Tasks' },
      {
        type: 'group', icon: Stamp, label: 'Notarization',
        children: [
          { href: '/backoffice/notarizations', label: 'Notarizations' },
          { href: '/backoffice/notaries', label: 'Notaries' },
        ],
      },
    ],
  },
  {
    label: 'Configuration',
    requiredRole: 'admin',
    items: [
      { type: 'link', href: '/backoffice/entities', icon: Building2, label: 'Entities' },
      { type: 'link', href: '/backoffice/tax_registration_types', icon: FileText, label: 'Tax Registration' },
      { type: 'link', href: '/backoffice/compliance_filing_types', icon: ClipboardCheck, label: 'Compliance Filing' },
      { type: 'link', href: '/backoffice/task_types', icon: ClipboardList, label: 'Tasks' },
      {
        type: 'group', icon: Users, label: 'Nominees',
        children: [
          { href: '/backoffice/nominee_director_types', label: 'Directors' },
          { href: '/backoffice/nominee_shareholder_types', label: 'Shareholders' },
        ],
      },
      {
        type: 'group', icon: Shield, label: 'Registered Agent',
        children: [
          { href: '/backoffice/registered_agent_types', label: 'Agents' },
          { href: '/backoffice/registered_address_types', label: 'Addresses' },
        ],
      },
      { type: 'link', href: '/backoffice/notarization_types', icon: Stamp, label: 'Notarization Types' },
      { type: 'link', href: '/backoffice/countries', icon: Globe, label: 'Jurisdictions' },
    ],
  },
  {
    label: 'People',
    items: [
      { type: 'link', href: '/backoffice/accounts', icon: Briefcase, label: 'Workspaces' },
      { type: 'link', href: '/backoffice/users', icon: Users, label: 'Users' },
      { type: 'link', href: '/backoffice/staff', icon: Settings, label: 'Staff', requiredRole: 'admin' },
      { type: 'link', href: '/backoffice/exports/contacts', icon: Download, label: 'Export Contacts', requiredRole: 'admin' },
    ],
  },
]

function BackofficeNav() {
  const { auth } = usePage<SharedProps>().props
  const isAdmin = auth.identity?.backoffice_admin ?? false
  const pathname = typeof window !== 'undefined' ? window.location.pathname : ''
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {}
    NAV_SECTIONS.forEach(section => {
      section.items.forEach(item => {
        if (item.type === 'group') {
          initial[item.label] = item.children.some(c => pathname.startsWith(c.href))
        }
      })
    })
    return initial
  })

  const linkClass = (href: string) =>
    cn(
      "flex items-center gap-2.5 rounded-sm px-2.5 py-1.5 text-[13px] transition-colors",
      pathname.startsWith(href)
        ? "bg-muted font-medium text-foreground"
        : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
    )

  function renderItem(item: NavEntry) {
    if (item.type === 'link') {
      return (
        <Link key={item.href} href={item.href} className={linkClass(item.href)}>
          <item.icon className="h-4 w-4 shrink-0 opacity-60" />
          {item.label}
        </Link>
      )
    }

    const isOpen = openGroups[item.label] ?? false
    const groupActive = item.children.some(c => pathname.startsWith(c.href))

    return (
      <div key={item.label}>
        <button
          onClick={() => setOpenGroups(prev => ({ ...prev, [item.label]: !prev[item.label] }))}
          className={cn(
            "flex w-full items-center gap-2.5 rounded-sm px-2.5 py-1.5 text-[13px] transition-colors",
            groupActive
              ? "font-medium text-foreground"
              : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
          )}
        >
          <item.icon className="h-4 w-4 shrink-0 opacity-60" />
          {item.label}
          <ChevronRight className={cn("ml-auto h-3.5 w-3.5 transition-transform", isOpen && "rotate-90")} />
        </button>
        {isOpen && (
          <div className="ml-4 mt-0.5 space-y-0.5 border-l border-border/40 pl-2">
            {item.children.map(child => (
              <Link key={child.href} href={child.href} className={linkClass(child.href)}>
                {child.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    )
  }

  // Filter sections and items based on role
  const filteredSections = NAV_SECTIONS
    .filter(section => (section.requiredRole ?? 'all') === 'all' || isAdmin)
    .map(section => ({
      ...section,
      items: section.items.filter(item => (item.requiredRole ?? 'all') === 'all' || isAdmin),
    }))
    .filter(section => section.items.length > 0)

  return (
    <nav className="px-2">
      {filteredSections.map((section, i) => (
        <div key={section.label || i}>
          {section.label && (
            <div className="px-2.5 pt-4 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60">
              {section.label}
            </div>
          )}
          <div className="space-y-0.5">
            {section.items.map(item => renderItem(item))}
          </div>
        </div>
      ))}
    </nav>
  )
}

function RoleBadge({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium bg-muted text-muted-foreground border border-border/40">
      {label}
    </span>
  )
}

export function BackofficeLayout({ children, title, breadcrumbs = [], contentWidth = 'default' }: BackofficeLayoutProps) {
  const { auth, flash } = usePage<SharedProps>().props
  const identity = auth.identity

  const roleBadges: string[] = []
  if (identity?.backoffice_admin) roleBadges.push('Admin')
  if (identity?.backoffice_compliance) roleBadges.push('Compliance')
  if (identity?.backoffice_support) roleBadges.push('Support')

  const defaultBreadcrumbs: BreadcrumbEntry[] = [
    { label: 'Backoffice', href: '/backoffice/dashboard' },
    { label: title },
  ]

  const finalBreadcrumbs = breadcrumbs.length > 0 ? breadcrumbs : defaultBreadcrumbs
  const contentClass = {
    default: 'max-w-2xl',
    wide: 'max-w-6xl',
    full: 'max-w-7xl',
  }[contentWidth]

  return (
    <>
      <Head title={`${title} — Backoffice`} />
      <div className="flex min-h-screen font-display">
        {/* Sidebar */}
        <aside className="w-56 shrink-0 border-r border-border/40 bg-muted/20">
          <div className="flex h-11 items-center gap-2 px-4">
            <span className="text-[13px] font-semibold text-muted-foreground tracking-wide">Backoffice</span>
            {roleBadges.map(badge => (
              <RoleBadge key={badge} label={badge} />
            ))}
          </div>
          <BackofficeNav />
          <div className="border-t border-border/40 mx-2 mt-3 pt-2 pb-2">
            <Link
              href="/accounts/select"
              className="flex items-center gap-2.5 rounded-sm px-2.5 py-1.5 text-[13px] text-muted-foreground hover:bg-muted/60 hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-4 w-4 shrink-0 opacity-60" />
              Back to App
            </Link>
          </div>
        </aside>

        {/* Main content */}
        <div className="flex-1 flex flex-col min-w-0">
          {(flash.notice || flash.alert) && (
            <div className="border-b border-border/40 px-6 py-3">
              {flash.notice && (
                <div className="max-w-2xl mx-auto rounded-lg bg-blue-50 border border-blue-200/60 p-3 text-sm text-blue-700">
                  {flash.notice}
                </div>
              )}
              {flash.alert && (
                <div className="max-w-2xl mx-auto rounded-lg bg-red-50 border border-red-200/60 p-3 text-sm text-red-700">
                  {flash.alert}
                </div>
              )}
            </div>
          )}

          <main className="flex-1 p-4 sm:p-6">
            <div className={cn(contentClass, "mx-auto w-full min-w-0")}>
              <Breadcrumb className="mb-4">
                <BreadcrumbList>
                  {finalBreadcrumbs.map((item, index) => (
                    <React.Fragment key={index}>
                      {index > 0 && <BreadcrumbSeparator />}
                      <BreadcrumbItem>
                        {item.href ? (
                          <BreadcrumbLink href={item.href}>{item.label}</BreadcrumbLink>
                        ) : (
                          <BreadcrumbPage>{item.label}</BreadcrumbPage>
                        )}
                      </BreadcrumbItem>
                    </React.Fragment>
                  ))}
                </BreadcrumbList>
              </Breadcrumb>
              {children}
            </div>
          </main>
        </div>
      </div>
    </>
  )
}
